import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { NavParams } from "ionic-angular";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import SortOptions from "../../../../models/content-wrapper/sort-options";
import CompanyResourceType from "../../../../models/resource-management/company-resource-type.enum";
import SelectFormInput from "../../../../models/SelectFormInput";
import { OrderDataService } from "../../../../providers/order-data-service/order-data-service";
import { ResourceManagementService } from "../../../../providers/resource-management/resource-management.service";
import { UtilityService } from "../../../../providers/utility-service/utility-service";
import { OrderForm } from "../order-form/order-form";

@Component({
  selector: "diagnosis-order-form",
  templateUrl: "diagnosis-order-form.html",
})
export class DiagnosisOrderFormComponent extends OrderForm implements OnInit {
  protected orderForm: FormGroup;
  protected incomingData: any = {
    category: "",
    physician: {},
    administeredBy: {},
  };

  private textAreaMaxLength = 1000;
  private startDateMin: string = "";
  private startDateMax: string = "";
  private endDateMin: string = "";
  private endDateMax: string = "";

  lodash = _;

  private _patientData: any = {};

  @Input() diagnosisOptions: SelectFormInput[] = [];
  @Input() diagnosisTypeOptions: SelectFormInput[] = [];
  @Input() physicianOptions: SelectFormInput[] = [];
  @Input() physicianOrderOptions: SelectFormInput[] = [];
  @Input() status: any;
  @Input() isOpenOrderEdit: boolean = false;

  @Input() set patientData(data: object) {
    this._patientData = data;
  }
  @Input() isIpad: boolean = false;
  @Output() onValidityChange: EventEmitter<any> = new EventEmitter();

  type: string;
  prefix: string;
  searching: boolean = false;
  searchTerm: string;
  hideDiagnosisOptions: boolean = true;
  displayedDiagnosisOptions: Array<{
    id: number;
    code: string;
    description: string;
  }> = [];
  diagnosisId: any;
  errorsFound: boolean = false;
  relatedDiagnosisUnchanged: boolean = false;
  duplicateDiagnoses: boolean = false;

  isEdit: boolean = false;
  relatedDiagnoses: any = FormArray;
  defaultStartTime: string = "";
  newPatientChartData: Object;
  dgPrimaryStartdate: string;
  dgSecondaryStartdate: string;

  hidePhysiciansOptions: Boolean = true;
  selectPhysicianFlag: boolean = false;
  setPhysicianName: boolean = false;

  hideDcPhysiciansOptions: Boolean = true;
  dcSearchTerm: string = "";
  selectDcPhysicianFlag: boolean = false;
  setDcPhysicianName: boolean = false;

  private physicians: any = [];

  constructor(
    private formBuilder: FormBuilder,
    private dataService: OrderDataService,
    private navParams: NavParams,
    private resourceManagementService: ResourceManagementService,
    public utilityService: UtilityService
  ) {
    super();

    this.startDateMin = moment()
      .subtract("10", "years")
      .format("YYYY-MM-DD")
      .toString();
    this.startDateMax = moment().format("YYYY-MM-DD").toString();
    this.endDateMin = moment().subtract("10", "years").format("YYYY-MM-DD");
    this.endDateMax = moment().add("10", "years").format("YYYY-MM-DD");
    this.defaultStartTime = moment().format("YYYY-MM-DD");

    this.relatedDiagnoses = this.formBuilder.array([]);
    this.addDiagnosis();

    this.orderForm = this.formBuilder.group({
      diagnosisType: ["", Validators.compose([Validators.required])],
      physician: ["", Validators.compose([Validators.required])],
      itemStartDatetime: [""],
      itemDiscontinueDatetime: [""],
      description: [""],
      physicianOrder: ["", Validators.compose([Validators.required])],
      physicianOrderNotes: [
        "",
        Validators.compose([Validators.maxLength(this.textAreaMaxLength / 2)]),
      ],
      relatedDiagnoses: this.relatedDiagnoses,
      searchTerm: new FormControl(""),
      dcSearchTerm: new FormControl(""),
    });

    this.orderForm.controls["diagnosisType"].valueChanges.subscribe((data) => {
      if (data === "PM" || data === "SC") {
        this.orderForm.addControl(
          "diagnosisText",
          new FormControl("", Validators.required)
        );
        this.orderForm.addControl(
          "diagnosisObject",
          new FormControl("", Validators.required)
        );
      } else {
        this.orderForm.removeControl("diagnosisText");
        this.orderForm.removeControl("diagnosisObject");
      }
      if (this.orderForm.controls["itemStartDatetime"].value) {
        this.orderForm.controls["itemStartDatetime"].setValue("");
      }
      this.type = data;
      if (this.newPatientChartData) {
        this.setStartMinDateOnType(data);
      }
    });

    //enforce startDate cannot be after endDate
    this.orderForm.controls["itemStartDatetime"].valueChanges.subscribe(
      (data) => {
        let startDate = moment(data).format("YYYY-MM-DD");
        let endValue = this.orderForm.controls["itemDiscontinueDatetime"].value;

        if (endValue.length > 0) {
          let endDate = moment(endValue).format("YYYY-MM-DD");
          if (moment(startDate).isAfter(endDate)) {
            this.orderForm.controls["itemStartDatetime"].setValue("");
          }
        }
        this.endDateMin = moment(data).format("YYYY-MM-DD");
      }
    );

    //enforce endDate cannot be before startDate
    this.orderForm.controls["itemDiscontinueDatetime"].valueChanges.subscribe(
      (data) => {
        let startValue = this.orderForm.controls["itemStartDatetime"].value;
        let endDate = moment(data).format("YYYY-MM-DD");

        if (startValue.length > 0) {
          let startDate = moment(startValue).format("YYYY-MM-DD");
          if (moment(startDate).isAfter(endDate)) {
            this.orderForm.controls["itemDiscontinueDatetime"].setValue("");
          }
        }
        this.startDateMax = moment(data).format("YYYY-MM-DD");
      }
    );

    //enforce character limit
    this.orderForm.controls["description"].valueChanges.subscribe((data) => {
      if (data.length > this.textAreaMaxLength) {
        this.orderForm.controls["description"].setValue(
          data.substring(0, this.textAreaMaxLength)
        );
      }
    });

    //enforce character limit
    this.orderForm.controls["physicianOrderNotes"].valueChanges.subscribe(
      (data) => {
        if (data.length > this.textAreaMaxLength / 2) {
          this.orderForm.controls["physicianOrderNotes"].setValue(
            data.substring(0, this.textAreaMaxLength / 2)
          );
        }
      }
    );
  }

  ngOnInit() {
    if (this.incomingData) {
      if (this.incomingData.rmPhysician) {
        this.searchTerm = this.incomingData.rmPhysician.displayName;
      }
    }

    if (this.status === "discontinue") {
      let discDate = this.orderForm.get("itemDiscontinueDatetime");
      discDate.setValidators([Validators.required]);
      discDate.updateValueAndValidity();
      this.orderForm.addControl(
        "dcPhysician",
        new FormControl("", Validators.required)
      );
      this.orderForm.controls["dcPhysician"].updateValueAndValidity();
    }

    this.newPatientChartData = this.navParams.get("newPatientChartData");
    if (this.newPatientChartData) {
      this.newPatientChartData["diagnosis"].map((diagnosis) => {
        if (diagnosis.active && diagnosis.diagnosisType === "PRIMARY") {
          this.dgPrimaryStartdate = moment(
            diagnosis.startDateAsLocalDateTime,
            "YYYY-MM-DD"
          ).format("MM-DD-YYYY");
        }
        if (diagnosis.active && diagnosis.diagnosisType === "SECONDARY") {
          this.dgSecondaryStartdate = moment(
            diagnosis.startDateAsLocalDateTime,
            "YYYY-MM-DD"
          ).format("MM-DD-YYYY");
        }
        if (this.status === "edit") {
          this.setStartMinDateOnType(this.incomingData["diagnosisType"]);
        }
      });
    }

    this.orderForm.valueChanges.subscribe((data) => {
      if (this.status !== "discontinue" && !this.isOpenOrderEdit) {
        this.setValidity();
      }
      this.onValidityChange.emit({
        validity: this.orderForm.valid,
        data: data,
      });
    });
  }

  /* --------------- Override data --------------- */
  @Input() set data(incomingData: object) {
    let newDiagnosis = incomingData["newDiagnosis"];
    this.incomingData = incomingData || {};
    let diagnosisType = this.incomingData["diagnosisType"];
    if (diagnosisType) {
      this.orderForm.controls["diagnosisType"].setValue(diagnosisType);
    }
    let keys = Object.keys(this.orderForm.controls);

    for (let key of keys) {
      if (incomingData[key]) {
        this.orderForm.controls[key].setValue(incomingData[key]);
      }
      if (key === "diagnosisObject" && newDiagnosis[0]) {
        this.setDiagnosis(newDiagnosis[0]);
      }

      if (
        key === "relatedDiagnoses" &&
        newDiagnosis &&
        newDiagnosis.length > 0
      ) {
        for (let i = 0; i < newDiagnosis.length; i++) {
          if (this.relatedDiagnoses.length < newDiagnosis.length) {
            this.addDiagnosis();
          }
          this.setRelatedDiagnosis(i, newDiagnosis[i]);
        }
      }
    }
  }

  setDiagnosis(diagnosis: any) {
    this.orderForm.controls["diagnosisText"].setValue(
      `${diagnosis.description} (${diagnosis.code})`
    );
    this.orderForm.controls["diagnosisObject"].setValue(diagnosis);
    this.diagnosisId = diagnosis.id;
    this.searchTerm = "";
    this.hideDiagnosisOptions = true;
  }

  /* --------------- Related Diagnoses --------------- */

  addDiagnosis(val: string = "", id: number = null): void {
    this.relatedDiagnoses.push(this.createRelatedDiagnosis(val, id));
  }

  createRelatedDiagnosis(val: string = "", id: number = null): FormGroup {
    return this.formBuilder.group({
      diagnosisText: [""],
      diagnosisObject: [""],
      searchTerm: [""],
      searchOptions: [[]],
      hideSearchOptions: [true],
      currentSearching: [false],
      id: [id],
    });
  }

  removeDiagnosis(index): void {
    this.relatedDiagnoses.removeAt(index);
  }

  setRelatedDiagnosis(index, diagnosis: any) {
    let relatedCtrl = this.relatedDiagnoses.controls[index];
    relatedCtrl.controls["diagnosisText"].setValue(
      `${diagnosis.description} (${diagnosis.code})`
    );
    relatedCtrl.controls["diagnosisObject"].setValue(diagnosis);
    relatedCtrl.controls["id"].setValue(diagnosis.id);
    relatedCtrl.controls["hideSearchOptions"].setValue(true);
  }

  /* --------------- Autocomplete --------------- */

  searchDiagnosisOptions(searchTerm: string) {
    searchTerm = searchTerm.toLowerCase();

    if (this.orderForm.controls["diagnosisObject"].value) {
      let currentDiagnosisDescription =
        this.orderForm.controls[
          "diagnosisObject"
        ].value.description.toLowerCase();
      if (!currentDiagnosisDescription.includes(searchTerm)) {
        this.orderForm.controls["diagnosisObject"].setValue(null);
      }
    }

    if (
      this.displayedDiagnosisOptions.filter(
        (diagnosis) => diagnosis.description.toLowerCase() === searchTerm
      ).length === 0 &&
      searchTerm.length >= 3
    ) {
      this.hideDiagnosisOptions = false;
      this.searching = true;
      this.searchTerm = searchTerm;
      this.dataService.getDiagnosisOptions(searchTerm).subscribe(
        (data) => {
          if (searchTerm === this.searchTerm) {
            this.displayedDiagnosisOptions = data;
            this.searching = false;
          }
        },
        (err) => {
          console.error(err);
          this.searching = false;
        }
      );
    } else {
      this.hideDiagnosisOptions = true;
    }
  }

  getCurrentDiagnosisCodes() {
    if (!this._patientData["patientChart"]) return null;

    let currentDiagnosis;
    let currentCode;
    let codes = [];

    //Get codes from PM Diagnosis from patient chart
    currentDiagnosis =
      this._patientData["patientChart"]["PrimaryDiagnosis"] || null;
    currentCode = this.filterOutCodeFromDGString(currentDiagnosis);
    //if code was found (not null) then add to codes array
    if (currentCode) codes.push(currentCode);

    //do same thing for SC DG
    currentDiagnosis =
      this._patientData["patientChart"]["SecondaryDiagnoses"] || null;
    currentCode = this.filterOutCodeFromDGString(currentDiagnosis);
    //if code was found (not null) then add to codes array
    if (currentCode) codes.push(currentCode);

    //add RL DG codes as well
    currentDiagnosis = this._patientData["patientChart"]["Comorbids"] || null;
    currentCode = this.getCodesFromRLDGString(currentDiagnosis); //here the value is an array
    if (currentCode) currentCode.forEach((code) => codes.push(code));

    // console.log("Current Diagnosis Codes from Patient Chart:", codes);

    return codes;
  }

  filterOutCodeFromDGString(diagnosis) {
    if (diagnosis) {
      let code = diagnosis.split(" ").pop();
      if (code.charAt(0) === "(") code = code.substring(1, code.length - 1); //trim () around code if there
      return code;
    }
    return null;
  }

  searchRelatedDiagnosisOptions(event, index) {
    let relatedCtrl = this.relatedDiagnoses.controls[index],
      searchTermCtrl = relatedCtrl.controls["searchTerm"],
      hideSearchOptionsCtrl = relatedCtrl.controls["hideSearchOptions"],
      currentSearchingCtrl = relatedCtrl.controls["currentSearching"],
      searchOptionsCtrl = relatedCtrl.controls["searchOptions"];

    let searchTerm = event.value.toLowerCase();

    if (relatedCtrl.controls["diagnosisObject"].value) {
      let currentDiagnosisDescription =
        relatedCtrl.controls["diagnosisObject"].value.description.toLowerCase();
      if (!currentDiagnosisDescription.includes(searchTerm)) {
        relatedCtrl.controls["diagnosisObject"].setValue(null);
      }
    }

    let displayedOptions = relatedCtrl.controls["searchOptions"].value;
    if (
      displayedOptions.filter(
        (diagnosis) => diagnosis.description.toLowerCase() === searchTerm
      ).length === 0 &&
      searchTerm.length >= 3
    ) {
      hideSearchOptionsCtrl.setValue(false);
      currentSearchingCtrl.setValue(true);
      searchTermCtrl.setValue(searchTerm);

      this.dataService.getDiagnosisOptions(searchTerm).subscribe(
        (data) => {
          data = data.filter((diagnosis) =>
            this.diagnosisNotSelected(
              `${diagnosis.description} (${diagnosis.code})`
            )
          );
          if (searchTerm === searchTermCtrl.value) {
            searchOptionsCtrl.setValue(data);
            currentSearchingCtrl.setValue(false);
          }
        },
        (err) => {
          console.error(err);
          currentSearchingCtrl.setValue(false);
        }
      );
    } else {
      hideSearchOptionsCtrl.setValue(true);
    }
  }

  diagnosisNotSelected(diagnosisDescription: string) {
    let arr = [];
    if (this.relatedDiagnoses) {
      for (let diagnosis of this.relatedDiagnoses.controls) {
        if (diagnosis.controls["diagnosisText"]) {
          let value = diagnosis.controls["diagnosisText"].value;
          arr.push(value);
        }
      }
    }
    return arr.indexOf(diagnosisDescription) === -1;
  }

  setValidity() {
    let orderForm = this.orderForm;
    this.errorsFound = false;
    this.relatedDiagnosisUnchanged = false;
    this.duplicateDiagnoses = false;

    if (this.type === "PM" || this.type === "SC") {
      if (this.doesDGHaveDuplicates()) {
        this.errorsFound = true;
        this.duplicateDiagnoses = true;
        this.orderForm.controls["diagnosisType"].setErrors({ incorrect: true });
      } else {
        this.orderForm.controls["diagnosisType"].setErrors(null);
      }
      let diagnosisObjectCtrl = orderForm.controls["diagnosisObject"],
        diagnosisTextCtrl = orderForm.controls["diagnosisText"];

      if (diagnosisObjectCtrl && diagnosisTextCtrl) {
        let diagnosisEmpty = diagnosisTextCtrl.value.length === 0,
          diagnosisValid =
            diagnosisObjectCtrl.value &&
            `${diagnosisObjectCtrl.value.description} (${diagnosisObjectCtrl.value.code})` ===
              diagnosisTextCtrl.value;

        if (diagnosisEmpty && !diagnosisValid) {
          diagnosisObjectCtrl.setErrors({ required: true });
          this.errorsFound = true;
        } else {
          if (!diagnosisValid) {
            diagnosisObjectCtrl.setErrors({ incorrect: true });
            this.errorsFound = true;
          } else {
            diagnosisObjectCtrl.setErrors(null);
          }
        }
      }
    } else if (this.type === "RL") {
      if (
        this.relatedDiagnoses &&
        this.relatedDiagnoses.controls &&
        this.relatedDiagnoses.controls.length > 0
      ) {
        if (!this.isRelatedDiagnosisUpdated()) {
          this.relatedDiagnosisUnchanged = true;
          this.errorsFound = true;
          this.orderForm.controls["diagnosisType"].setErrors({
            incorrect: true,
          });
        } else if (this.doesDGHaveDuplicates()) {
          this.errorsFound = true;
          this.duplicateDiagnoses = true;
          this.orderForm.controls["diagnosisType"].setErrors({
            incorrect: true,
          });
        } else {
          this.orderForm.controls["diagnosisType"].setErrors(null);
        }

        for (let relatedDiagnosis of this.relatedDiagnoses.controls) {
          let diagnosisObject =
              relatedDiagnosis.controls["diagnosisObject"].value,
            diagnosisText = relatedDiagnosis.controls["diagnosisText"].value;

          let relatedDiagnosisEmpty = diagnosisText.length === 0,
            relatedDiagnosisValid =
              diagnosisObject &&
              `${diagnosisObject.description} (${diagnosisObject.code})` ===
                diagnosisText;

          if (relatedDiagnosisEmpty || !relatedDiagnosisValid) {
            relatedDiagnosis.setErrors({ incorrect: true });
            this.errorsFound = true;
          } else {
            relatedDiagnosis.setErrors(null);
          }
        }
      }
    }
  }

  isRelatedDiagnosisUpdated() {
    let relatedDiagnosisText = this.relatedDiagnoses.controls
      .map((rldg) => {
        return rldg.controls["diagnosisText"].value;
      })
      .toString();
    let currentCodes = this.getCodesFromRLDGString(relatedDiagnosisText);
    let codesFromChart = null;
    if (this._patientData["patientChart"]) {
      codesFromChart = this.getCodesFromRLDGString(
        this._patientData["patientChart"]["Comorbids"]
      );
    }
    let isUpdated = false;
    if (!codesFromChart) return true;
    codesFromChart.forEach((code) => {
      if (currentCodes && currentCodes.indexOf(code) === -1) isUpdated = true;
    });
    //if there are matches for the codes on file, if there is a new rldg it's still updated
    if (
      this.relatedDiagnoses.controls.length > codesFromChart.length ||
      !relatedDiagnosisText
    )
      isUpdated = true;
    return isUpdated;
  }

  //checks to see if any values in DG are duplicates of current PM or SC from patient chart
  doesDGHaveDuplicates() {
    if (
      this.orderForm.controls["diagnosisType"].value === "PM" ||
      this.orderForm.controls["diagnosisType"].value === "SC"
    ) {
      let currentCodes = this.getCurrentDiagnosisCodes();
      if (
        currentCodes &&
        this.orderForm.get("diagnosisObject") &&
        this.orderForm.get("diagnosisObject").value
      ) {
        return currentCodes.includes(
          this.orderForm.get("diagnosisObject").value["code"]
        );
      }
    } else {
      //grab codes from controls
      if (!this.relatedDiagnoses.controls) return false;
      let relatedDiagnosisText = this.relatedDiagnoses.controls
        .map((rldg) => {
          return rldg.controls["diagnosisText"].value;
        })
        .toString();
      let currentCodes = this.getCodesFromRLDGString(relatedDiagnosisText);
      if (!currentCodes) return false;

      //check against PM DG from chart
      if (this._patientData["patientChart"]) {
        let dgFromChart = this._patientData["patientChart"]["PrimaryDiagnosis"];
        let codeFromChart = this.filterOutCodeFromDGString(dgFromChart);
        if (currentCodes.includes(codeFromChart)) return true;

        //do same thing for SC DG
        dgFromChart = this._patientData["patientChart"]["SecondaryDiagnoses"];
        codeFromChart = this.filterOutCodeFromDGString(dgFromChart);
        if (currentCodes.includes(codeFromChart)) return true;

        //RLDG check as well (assuming no full replacement strategy)
        dgFromChart = this._patientData["patientChart"]["Comorbids"];
        codeFromChart = this.getCodesFromRLDGString(dgFromChart);
        if (codeFromChart) {
          return codeFromChart.some((code) => currentCodes.includes(code));
        } else return false;
      }
    }
    return false;
  }

  getCodesFromRLDGString(input: string) {
    if (!input) return null;
    return input.split(",").map((dg) => this.filterOutCodeFromDGString(dg));
  }

  setDefaultStartDateMin() {
    this.startDateMin = moment().subtract("10", "years").format("YYYY-MM-DD");
  }

  setStartMinDateOnType(type) {
    switch (type) {
      case "PM":
        if (this.dgPrimaryStartdate) {
          this.startDateMin = moment(
            this.dgPrimaryStartdate,
            "MM-DD-YYYY"
          ).format("YYYY-MM-DD");
        } else {
          this.setDefaultStartDateMin();
        }
        break;
      case "SC":
        if (this.dgSecondaryStartdate) {
          this.startDateMin = moment(
            this.dgSecondaryStartdate,
            "MM-DD-YYYY"
          ).format("YYYY-MM-DD");
        } else {
          this.setDefaultStartDateMin();
        }
        break;
      case "RL":
        this.setDefaultStartDateMin();
        break;
    }
  }

  /**
   * @param searchTerm user enters to search the physician, when the term length is > 2 at that time call that getPhysicians api
   */
  searchPhysicians(searchTerm: string) {
    if (searchTerm !== "") {
      this.hidePhysiciansOptions = false;
      if (searchTerm.length > 2) {
        this.getPhysicians(searchTerm);
      }
    }
  }

  /**
   * @param searchTerm search term to get physicians
   */
  getPhysicians(searchTerm) {
    if (!this.selectPhysicianFlag) {
      this.selectPhysicianFlag = false;
      this.setPhysicianName = true;
      this.resourceManagementService
        .getCompanyResourceSummaries(
          0,
          10000,
          searchTerm,
          new SortOptions("last_name", "asc"),
          CompanyResourceType.PROVIDER
        )
        .subscribe(
          (response) => {
            this.physicians = [];
            let providers = _.filter(response.content, (x) => x.externalId); // valid external Id.
            this.physicians = this.physicians.concat(providers);
          },
          (err) => {
            this.utilityService.presentErrorToast(
              err,
              "Failed to Load Providers"
            );
          }
        );
    } else {
      this.selectPhysicianFlag = false;
    }
  }

  setPhysician(physician: any) {
    this.selectPhysicianFlag = true;
    this.setPhysicianName = false;
    this.hidePhysiciansOptions = true;
    this.orderForm.controls["physician"].setValue(physician);
    this.orderForm.controls["searchTerm"].setValue(physician.displayName);
  }

  /**
   * @param searchTerm user enters to search the physician, when the term length is > 2 at that time call that getPhysicians api
   */
  searchDcPhysicians(searchTerm: string) {
    if (searchTerm !== "") {
      this.hideDcPhysiciansOptions = false;
      if (searchTerm.length > 2) {
        this.getDcPhysicians(searchTerm);
      }
    }
  }

  /**
   * @param searchTerm search term to get physicians
   */
  getDcPhysicians(searchTerm) {
    if (!this.selectDcPhysicianFlag) {
      this.selectDcPhysicianFlag = false;
      this.setDcPhysicianName = true;
      this.resourceManagementService
        .getCompanyResourceSummaries(
          0,
          10000,
          searchTerm,
          new SortOptions("last_name", "asc"),
          CompanyResourceType.PROVIDER
        )
        .subscribe(
          (response) => {
            this.physicians = [];
            let providers = _.filter(response.content, (x) => x.externalId); // valid external Id.
            this.physicians = this.physicians.concat(providers);
          },
          (err) => {
            this.utilityService.presentErrorToast(
              err,
              "Failed to Load Providers"
            );
          }
        );
    } else {
      this.selectDcPhysicianFlag = false;
    }
  }

  setDcPhysician(physician: any) {
    this.selectDcPhysicianFlag = true;
    this.setDcPhysicianName = false;
    this.hideDcPhysiciansOptions = true;
    this.orderForm.controls["dcPhysician"].setValue(physician);
    this.orderForm.controls["dcSearchTerm"].setValue(physician.displayName);
  }
}



// WEBPACK FOOTER //
// ./src/components/orders/forms/diagnosis-order-form/diagnosis-order-form.ts